import { CallStatus } from "@prisma/client";
import { QueryClient } from "@tanstack/react-query";
import { ReactNode } from "react";
import IconCart from "@/app/(components)/generics/Icons/IconCart";
import IconCompass from "@/app/(components)/generics/Icons/IconCompass";
import IconObjection from "@/app/(components)/generics/Icons/IconObjection";
import IconPain from "@/app/(components)/generics/Icons/IconPain";
import IconQuote from "@/app/(components)/generics/Icons/IconQuote";
import IconZap from "@/app/(components)/generics/Icons/IconZap";
import { buildCallsQueryKey } from "@/app/hooks/fetchs/calls/useFetchCalls";
import { TKey } from "@/app/translations/TKey";
import { CallAnalysisCategory } from "@/app/types/call/CallAnalysis";
import { CallAnalysisBootstrap } from "@/app/types/call/CallAnalysisBootstrap";
import { CallAnalysisWorkspace } from "@/app/types/call/CallAnalysisWorkspace";
import { CallFile } from "@/app/types/call/CallFile";
import { CallScopeFilter } from "@/app/types/call/CallScopeFilter";
import { GetUserCallsResult } from "@/app/types/call/GetUserCallsResult";
import { UserCall } from "@/app/types/call/UserCall";
import { UFile } from "@/app/utils/UFile";
import { GlobalEvent } from "@/app/vars/Events";
import { Globals } from "@/app/vars/Globals";
import { QueryKey } from "@/app/vars/QueryKeys";

export type CallStatusConfig = {
  key: TKey;
  cssClass: string;
};

export const CALL_STATUS_MAP: Partial<Record<CallStatus, CallStatusConfig>> = {
  [CallStatus.UPLOADED]: { key: TKey.CALL_ANALYSIS_STATUS_UPLOADED, cssClass: "statusPending" },
  [CallStatus.ANALYZING]: {
    key: TKey.CALL_ANALYSIS_STATUS_ANALYZING,
    cssClass: "statusProcessing",
  },
  [CallStatus.ANALYZED]: { key: TKey.CALL_ANALYSIS_STATUS_ANALYZED, cssClass: "statusDone" },
  [CallStatus.FAILED]: { key: TKey.CALL_ANALYSIS_STATUS_FAILED, cssClass: "statusError" },
};

let tempIdCounter = 0;
function nextTempId(): number {
  return --tempIdCounter;
}

export function resolveInitialWorkspace(
  bootstrap: CallAnalysisBootstrap | null,
  projectId: number | undefined,
  offerId: number | undefined,
  page: number,
  status: CallStatus | null | undefined,
  filter: CallScopeFilter,
): CallAnalysisWorkspace | undefined {
  if (!bootstrap || bootstrap.projectId !== projectId || bootstrap.offerId !== offerId) {
    return undefined;
  }

  const serverKey = buildCallsQueryKey(
    bootstrap.projectId,
    bootstrap.offerId,
    1,
    Globals.CALLS_PER_PAGE,
    bootstrap.status,
    bootstrap.filter,
  );
  const clientKey = buildCallsQueryKey(
    projectId,
    offerId,
    page,
    Globals.CALLS_PER_PAGE,
    status,
    filter,
  );

  return serverKey.every((value, index) => value === clientKey[index])
    ? bootstrap.workspace
    : undefined;
}

export function filterAndMapNewFiles(newFiles: File[], existingFiles: CallFile[]): CallFile[] {
  const valid = newFiles.filter((f) => UFile.isCallFile(f) && f.size <= UFile.getMaxCallFileSize());
  const deduped = valid.filter(
    (f) =>
      !existingFiles.some(
        (existing) => existing.file?.name === f.name && existing.file?.size === f.size,
      ),
  );

  const limit = UFile.getMaxCallFilesPerUpload();
  return deduped.slice(0, limit).map((f) => ({
    id: nextTempId(),
    file: f,
    label: UFile.getNameWithoutExtension(f.name),
    status: undefined,
    progress: 0,
    callDate: new Date(f.lastModified),
  }));
}

export function mapSavedCallsToCallFiles(savedCalls: UserCall[]): CallFile[] {
  return savedCalls.map((c) => ({
    id: c.id,
    status: c.status,
    label: c.name,
    blobUrl: c.fileUrl,
    errorId: c.errorId,
    meta: c.analysis?.metadata
      ? {
          mainTopic: (c.analysis.metadata as any).mainTopic ?? null,
          mainBlocker: (c.analysis.metadata as any).mainBlocker ?? null,
          nextStepDetected: (c.analysis.metadata as any).nextStepDetected ?? null,
        }
      : null,
    scores: c.analysis
      ? {
          pain: c.analysis.scorePain,
          objection: c.analysis.scoreObjection,
          buyingSignal: c.analysis.scoreBuyingSignal,
          marketGlobal: c.analysis.scoreMarketGlobal,
          conversationClarity: c.analysis.scoreConversationClarity,
          prospectEngagement: c.analysis.scoreProspectEngagement,
          nextStepClarity: c.analysis.scoreNextStepClarity,
          callGlobal: c.analysis.scoreCallGlobal,
          global: c.analysis.scoreGlobal,
        }
      : null,
    savedCall: {
      id: c.id,
      projectId: c.projectId,
      name: c.name,
      type: c.type,
      fileUrl: c.fileUrl,
      fileType: c.fileType,
      fileSize: c.fileSize,
      callDate: c.callDate,
      note: c.note,
      duration: c.duration,
      createdAt: c.createdAt,
      uploaderId: c.uploaderId,
      excludedFromAnalysis: c.excludedFromAnalysis,
      member: c.member,
    },
  }));
}
export type CategoryStyle = {
  color: string;
  bg: string;
};

export function getCategoryStyle(category: CallAnalysisCategory): CategoryStyle {
  switch (category) {
    case CallAnalysisCategory.SUMMARY:
      return { color: "var(--color-green-3)", bg: "var(--color-green-4)" };
    case CallAnalysisCategory.PAIN:
      return { color: "var(--color-analysis-pain)", bg: "var(--color-analysis-pain-bg)" };
    case CallAnalysisCategory.OBJECTIONS:
      return {
        color: "var(--color-analysis-objection)",
        bg: "var(--color-analysis-objection-bg)",
      };
    case CallAnalysisCategory.SIGNALS:
      return { color: "var(--color-analysis-signal)", bg: "var(--color-analysis-signal-bg)" };
    case CallAnalysisCategory.VERBATIMS:
      return {
        color: "var(--color-analysis-verbatim)",
        bg: "var(--color-analysis-verbatim-bg)",
      };
    case CallAnalysisCategory.PRODUCT:
      return {
        color: "var(--color-analysis-feature)",
        bg: "var(--color-analysis-feature-bg)",
      };
    case CallAnalysisCategory.COMPETITORS:
      return {
        color: "var(--color-analysis-competitor)",
        bg: "var(--color-analysis-competitor-bg)",
      };
    default:
      return { color: "var(--color-black-1)", bg: "var(--color-white-3)" };
  }
}

export const getCategoryIcon = (category: CallAnalysisCategory): ReactNode => {
  switch (category) {
    case CallAnalysisCategory.PAIN:
      return <IconPain />;
    case CallAnalysisCategory.OBJECTIONS:
      return <IconObjection />;
    case CallAnalysisCategory.SIGNALS:
      return <IconCart />;
    case CallAnalysisCategory.PRODUCT:
      return <IconZap />;
    case CallAnalysisCategory.VERBATIMS:
      return <IconQuote />;
    case CallAnalysisCategory.COMPETITORS:
      return <IconCompass />;
    default:
      return null;
  }
};

export function getScoreKey(score: number): TKey {
  if (score >= 80) return TKey.CALL_ANALYSIS_SCORE_VERY_HIGH;
  if (score >= 65) return TKey.CALL_ANALYSIS_SCORE_HIGH;
  if (score >= 50) return TKey.CALL_ANALYSIS_SCORE_MEDIUM;
  if (score >= 35) return TKey.CALL_ANALYSIS_SCORE_LOW;
  return TKey.CALL_ANALYSIS_SCORE_VERY_LOW;
}

export type CallLevelConfig = {
  key: TKey;
  levelClass: string;
};

export const CALL_LEVEL_MAP: Record<string, CallLevelConfig> = {
  "0": { key: TKey.CALL_ANALYSIS_LEVEL_LOW, levelClass: "level0" },
  low: { key: TKey.CALL_ANALYSIS_LEVEL_LOW, levelClass: "level0" },
  "1": { key: TKey.CALL_ANALYSIS_LEVEL_MEDIUM, levelClass: "level1" },
  medium: { key: TKey.CALL_ANALYSIS_LEVEL_MEDIUM, levelClass: "level1" },
  "2": { key: TKey.CALL_ANALYSIS_LEVEL_HIGH, levelClass: "level2" },
  high: { key: TKey.CALL_ANALYSIS_LEVEL_HIGH, levelClass: "level2" },
};

export function getLevelConfig(level?: number | string | null): CallLevelConfig | null {
  if (level === undefined || level === null) return null;
  return CALL_LEVEL_MAP[String(level).toLowerCase()] || null;
}

export function getCategoryEmptyKey(category?: CallAnalysisCategory): TKey {
  switch (category) {
    case CallAnalysisCategory.PAIN:
      return TKey.CALL_ANALYSIS_EMPTY_PAIN;
    case CallAnalysisCategory.OBJECTIONS:
      return TKey.CALL_ANALYSIS_EMPTY_OBJECTION;
    case CallAnalysisCategory.SIGNALS:
      return TKey.CALL_ANALYSIS_EMPTY_SIGNAL;
    case CallAnalysisCategory.VERBATIMS:
      return TKey.CALL_ANALYSIS_EMPTY_VERBATIM;
    case CallAnalysisCategory.PRODUCT:
      return TKey.CALL_ANALYSIS_EMPTY_PRODUCT;
    case CallAnalysisCategory.COMPETITORS:
      return TKey.CALL_ANALYSIS_EMPTY_COMPETITOR;
    default:
      return TKey.CALL_ANALYSIS_EMPTY;
  }
}

export function getCallDetailEmptyKey(category: CallAnalysisCategory): TKey {
  switch (category) {
    case CallAnalysisCategory.PAIN:
      return TKey.CALL_DETAIL_EMPTY_PAIN;
    case CallAnalysisCategory.OBJECTIONS:
      return TKey.CALL_DETAIL_EMPTY_OBJECTION;
    case CallAnalysisCategory.SIGNALS:
      return TKey.CALL_DETAIL_EMPTY_SIGNAL;
    case CallAnalysisCategory.VERBATIMS:
      return TKey.CALL_DETAIL_EMPTY_VERBATIM;
    case CallAnalysisCategory.PRODUCT:
      return TKey.CALL_DETAIL_EMPTY_PRODUCT;
    case CallAnalysisCategory.COMPETITORS:
      return TKey.CALL_DETAIL_EMPTY_COMPETITOR;
    default:
      return TKey.CALL_ANALYSIS_EMPTY;
  }
}

export function handleAnalysisStartedOptimistic(
  queryClient: QueryClient,
  callId: number,
  projectId?: number,
) {
  // Optimistic update of the calls cache for instant UI feedback (partial match with exact: false)
  queryClient.setQueriesData<GetUserCallsResult>(
    { queryKey: [QueryKey.CALLS], exact: false },
    (oldData) => {
      if (!oldData?.calls) return oldData;
      return {
        ...oldData,
        calls: oldData.calls.map((c) =>
          c.id === callId ? { ...c, status: CallStatus.ANALYZING } : c,
        ),
      };
    },
  );

  // Dispatch global event for instant decoupled sibling notification (e.g. CallDetail modal body)
  window.dispatchEvent(
    new CustomEvent(GlobalEvent.CALL_STATUS_UPDATED, {
      detail: { id: callId, status: CallStatus.ANALYZING },
    }),
  );

  // Trigger immediate invalidation to sync both page list and modal detail view
  queryClient.invalidateQueries({ queryKey: [QueryKey.CALLS] });
  queryClient.invalidateQueries({ queryKey: [QueryKey.RUNNING_JOBS] });
  if (projectId) {
    queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECT_INSIGHTS, projectId] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECT_PATTERNS, projectId] });
    queryClient.invalidateQueries({
      queryKey: [QueryKey.PROJECT_RECOMMENDATIONS, projectId],
    });
  }
}
